import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LOGO_FULL, LOGO_DIAMOND, NAV } from "@/lib/site";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? "border-[#222] bg-[#050505]/90 backdrop-blur-xl" : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 md:px-10">
        <a href="#pocetna" data-testid="header-logo" className="flex items-center gap-3">
          <img src={LOGO_DIAMOND} alt="Onarf Marketing" className="h-8 w-8 object-contain" />
          <span className="font-display text-sm font-black uppercase tracking-[0.18em] text-white md:text-base">
            Onarf
          </span>
        </a>

        <nav className="hidden items-center gap-9 lg:flex">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.href.replace("#", "")}`}
              className="font-display text-xs font-bold uppercase tracking-[0.18em] text-white/70 transition-colors duration-200 hover:text-[#50C878]"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <a
          href="#kontakt"
          data-testid="header-cta"
          className="group hidden items-center gap-2 rounded-none bg-[#50C878] px-6 py-3 font-display text-xs font-extrabold uppercase tracking-[0.14em] text-black transition-colors duration-300 hover:bg-[#3EB264] lg:flex"
        >
          Zakažite konzultacije
          <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="text-white lg:hidden"
          aria-label="Izbornik"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="overflow-hidden border-t border-[#222] bg-[#050505] lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-6">
              {NAV.map((n) => (
                <a
                  key={n.href}
                  href={n.href}
                  data-testid={`mobile-nav-${n.href.replace("#", "")}`}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 border-b border-[#161616] py-4 font-display text-lg font-extrabold uppercase tracking-wide text-white"
                >
                  <img src={LOGO_DIAMOND} alt="" className="diamond-bullet" />
                  {n.label}
                </a>
              ))}
              <a
                href="#kontakt"
                onClick={() => setOpen(false)}
                data-testid="mobile-cta"
                className="mt-5 flex items-center justify-center gap-2 bg-[#50C878] px-6 py-4 font-display text-sm font-extrabold uppercase tracking-widest text-black"
              >
                Zakažite konzultacije <ArrowUpRight size={16} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
